import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { FollowUpDto } from '@ecom/contracts';
import { StagePill } from '@/components/customer-stage';
import { FilterBar } from '@/components/filters';
import { FollowUpSheet } from '@/components/follow-up-sheet';
import { ClockIcon, PlusIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Table, TableScroll, Tabs, Td, Th, Tr } from '@/components/page';
import { Alert, Button, EmptyState, Pill, TableSkeleton } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { dateTime } from '@/lib/format';
import { can } from '@/lib/session';
import { useAuthStore } from '@/stores/auth.store';
import { useFollowUpsStore, type FollowUpView } from '@/stores/follow-ups.store';

/**
 * Follow-ups (ADR-104): what must happen next, by whom and when. Mine first —
 * the bell's count opens here — then everyone's, then what is done. Open ones
 * run by day, so the overdue lead; a row opens the follow-up to change it.
 */
export function FollowUpsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const mayWrite = useAuthStore((state) => can(state.user, 'support:manage'));
  const { view, search, items, nextCursor, total, loading, loadingMore, error, load, loadMore, done, reopen } = useFollowUpsStore();
  const [editing, setEditing] = useState<FollowUpDto | 'new' | null>(null);
  const [params] = useSearchParams();
  const linked = params.get('view');

  useEffect(() => {
    void load(linked === 'everyone' || linked === 'done' || linked === 'mine' ? { view: linked } : undefined);
  }, [load, linked]);

  const add = mayWrite ? (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('followUpAdd')}
    </Button>
  ) : null;

  return (
    <Page title={t('followUps')} subtitle={t('followUpsSubtitle')} actions={add} fill>
      <ListCard>
        <Tabs
          className="px-2"
          label={t('followUps')}
          value={view}
          onChange={(next: FollowUpView) => void load({ view: next })}
          items={[
            { id: 'mine', label: t('followUpsMine') },
            { id: 'everyone', label: t('followUpsEveryone') },
            { id: 'done', label: t('followUpsDone') },
          ]}
        />
        <ListToolbar>
          <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('followUpsSearch')} total={total ?? undefined} shown={items.length} />
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={5} />
        ) : items.length === 0 ? (
          <EmptyState title={t('followUpsEmpty')} body={t('followUpsEmptyHint')} icon={<ClockIcon />} action={add ?? undefined} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('followUpTitle')}</Th>
                  <Th>{t('followUpAbout')}</Th>
                  <Th>{t('followUpDue')}</Th>
                  <Th>{t('followUpAssignee')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <Tr key={row.id} onClick={mayWrite ? () => setEditing(row) : undefined}>
                    <Td className="max-w-96">
                      <span dir="auto" className="block font-medium text-ink">{row.title}</span>
                      {row.note ? <span dir="auto" className="line-clamp-2 block text-xs text-ink-muted">{row.note}</span> : null}
                      <span className="block text-xs text-ink-muted">
                        {row.doneAt && row.doneBy
                          ? t('followUpDoneBy', { name: row.doneBy, date: dateTime(row.doneAt, locale) })
                          : t('followUpCreatedBy', { name: row.createdBy })}
                      </span>
                    </Td>
                    <Td>
                      <span className="flex flex-col items-start gap-1">
                        {row.customer ? (
                          <span className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/customers/${row.customer.id}`}
                              onClick={(event) => event.stopPropagation()}
                              className="font-medium text-ink hover:text-brand-700 hover:underline"
                            >
                              {row.customer.name}
                            </Link>
                            <StagePill stage={row.customer.stage} />
                          </span>
                        ) : null}
                        {row.order ? (
                          <Link
                            to={`/orders/${row.order.id}`}
                            onClick={(event) => event.stopPropagation()}
                            dir="ltr"
                            className="text-xs text-brand-700 hover:underline"
                          >
                            {row.order.number}
                          </Link>
                        ) : null}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap">
                      <span className="flex flex-col items-start gap-1">
                        <span className="text-ink">{dateTime(row.dueAt, locale)}</span>
                        {row.overdue ? <Pill tone="danger">{t('followUpOverdue')}</Pill> : null}
                      </span>
                    </Td>
                    <Td className="text-ink-muted">{row.assignee?.name ?? t('followUpNobody')}</Td>
                    <Td align="end">
                      {mayWrite ? (
                        <Button
                          variant={row.doneAt ? 'ghost' : 'secondary'}
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            void (row.doneAt ? reopen(row.id) : done(row.id));
                          }}
                        >
                          {row.doneAt ? t('followUpReopen') : t('followUpMarkDone')}
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? <FollowUpSheet followUp={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Page>
  );
}
